import React, { useState } from "react";
import { Button, Typography, CircularProgress, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { toast } from "react-toastify";
import CustomModal from "../../../../common-utilities/core/modal";
import { HTTP_METHODS, invokeApi, invokeUploadFile } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import "./bulk-po-modal.scss";

const STAT_CATEGORIES = [
  {
    key: "created",
    label: "POs Created",
    tone: "green",
    count: (result) => result.summary?.poCreatedCount ?? 0,
    items: (result) => result.createdPOs || [],
    renderItem: (po, idx) => (
      <li key={idx}>
        Store : {po.storeId} - Vendor : {po.vendorCode} - PO : {po.poNumber}
      </li>
    ),
  },
  {
    key: "skippedStores",
    label: "Stores Skipped",
    tone: "amber",
    count: (result) => (result.summary?.skippedStoresCount ?? 0) + (result.summary?.unmatchedStoresCount ?? 0),
    items: (result) => [...(result.skippedStores || []), ...(result.unmatchedStores || [])],
    renderItem: (s, idx) => (
      <li key={idx}>
        Store {s.storeId}
        {s.storeCode ? ` (${s.storeCode})` : ""}: {s.reason}
      </li>
    ),
  },
  {
    key: "skippedItems",
    label: "Items Skipped",
    tone: "amber",
    count: (result) => result.summary?.skippedItemsCount ?? 0,
    items: (result) => result.skippedItems || [],
    renderItem: (s, idx) => (
      <li key={idx}>
        Store {s.storeId} - {s.itemName || s.prItemId}: {s.reason}
      </li>
    ),
  },
  {
    key: "failedGroups",
    label: "Groups Failed",
    tone: "amber",
    count: (result) => result.summary?.failedGroupsCount ?? 0,
    items: (result) => result.failedGroups || [],
    renderItem: (f, idx) => (
      <li key={idx}>
        Store {f.storeId} / Vendor {f.vendorCode}: {f.reason}
      </li>
    ),
  },
  {
    key: "vendorErrors",
    label: "Vendor Errors",
    tone: "amber",
    count: (result) => result.summary?.vendorResolutionErrorsCount ?? 0,
    items: (result) => result.vendorResolutionErrors || [],
    renderItem: (v, idx) => (
      <li key={idx}>
        Store {v.storeId} - {v.itemName || v.prItemId} / Vendor {v.vendorCode}: {v.reason}
      </li>
    ),
  },
];

const BulkPOModal = ({ prId, onClose, onGenerated }) => {
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Same choice as the manual create-PO screen's "Save as draft" / "Create order" buttons
  // (traced: both call the same onSave -> POST /purchase-order, differing only in a
  // createApproval=true query param) -- made once here, before generation, applied to every
  // PO the run actually creates. Defaults to draft, matching this feature's behavior before
  // this choice existed.
  const [submitMode, setSubmitMode] = useState("draft");
  // Only one category's detail list is shown at a time -- a single active key, not a
  // per-category boolean map. Clicking the already-active card's own key collapses it back.
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleExpand = (key) => setExpandedCategory((prev) => (prev === key ? null : key));

  const downloadTemplate = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_BILLING_LOCATION_TEMPLATE}/${prId}`,
      null,
      null,
      null,
      "blob"
    )
      .then((response) => {
        if (response.code) {
          toast.error(response.message || "Failed to download template");
          return;
        }
        const _url = window.URL.createObjectURL(response);
        const alink = document.createElement("a");
        alink.href = _url;
        alink.download = "Bulk_PO_Allocation_Template.xlsx";
        alink.click();
      })
      .catch(() => toast.error("Failed to download template"));
  };

  const generateBulkPOs = () => {
    if (!file) {
      toast.error("Please choose the filled allocation file first");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setIsSubmitting(true);
    const url = `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_BULK_CONVERT_TO_PO}${prId}${
      submitMode === "approval" ? "?createApproval=true" : ""
    }`;
    invokeUploadFile(url, formData)
      .then((response) => setResult(response.data))
      .catch((err) => toast.error(err?.response?.data?.message || "Bulk PO generation failed"))
      .finally(() => setIsSubmitting(false));
  };

  if (result) {
    const listStyle = { maxHeight: 160, overflowY: "auto", margin: "8px 0 0", paddingLeft: 20 };
    // Card is a flat sibling of every other card -- none of the five is nested inside a
    // wrapper shared with only some of the others, so the row never collapses into a column.
    const renderCard = (cat) => {
      const count = cat.count(result);
      const isExpandable = count > 0;
      const isExpanded = isExpandable && expandedCategory === cat.key;
      const toneClass = isExpandable ? cat.tone : "";
      return (
        <button
          key={cat.key}
          type="button"
          className={`summary-card ${toneClass}${isExpanded ? " expanded" : ""}${!isExpandable ? " disabled-expand" : ""}`}
          onClick={() => isExpandable && toggleExpand(cat.key)}
          disabled={!isExpandable}
          aria-expanded={isExpandable ? isExpanded : undefined}
        >
          <span className="card-indicator" />
          <span className="card-label">{cat.label}</span>
          <span className="card-count">{count}</span>
        </button>
      );
    };
    // The detail panel is decoupled from the cards entirely -- it renders once, full width,
    // below the whole row, for whichever single category is currently expanded.
    const activeCategory = STAT_CATEGORIES.find((cat) => cat.key === expandedCategory && cat.count(result) > 0);
    return (
      <CustomModal title="Bulk PO Generation Result" disabledOutsideClick onClose={onClose}>
        <div className="finance-v2-b-modal-form-container">
          {(result.summary?.poCreatedCount ?? 0) > 0 && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {submitMode === "draft"
                ? "Drafts were created — the POs above were saved as drafts and were not submitted for approval."
                : "The POs above were submitted for approval, not saved as drafts."}
            </Typography>
          )}
          <div className="bulk-po-result-summary">
            <div className="summary-cards-row">{STAT_CATEGORIES.map(renderCard)}</div>

            {activeCategory && (
              <div className="row-detail">
                <Typography className="row-detail-title">{activeCategory.label}</Typography>
                <ul style={listStyle}>
                  {activeCategory.items(result).map((item, idx) => activeCategory.renderItem(item, idx))}
                </ul>
              </div>
            )}
          </div>

          {result.allocationUploadErrors?.length > 0 && (
            <>
              <Typography className="section-title">Allocation file errors</Typography>
              <ul style={listStyle}>
                {result.allocationUploadErrors.map((e, idx) => (
                  <li key={idx}>
                    {e.row ? `Row ${e.row}` : `Store ${e.storeId}`}
                    {e.storeCode ? ` (${e.storeCode})` : ""}
                    {e.itemCode ? ` [${e.itemCode}]` : ""}: {e.reason}
                  </li>
                ))}
              </ul>
            </>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              onGenerated && onGenerated();
              onClose();
            }}
          >
            Done
          </Button>
        </div>
      </CustomModal>
    );
  }

  return (
    <CustomModal title="Bulk PO Generation" disabledOutsideClick={isSubmitting} onClose={isSubmitting ? undefined : onClose}>
      <div className="finance-v2-b-modal-form-container">
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          Generates one Purchase Order per store/vendor combination for this PR's target stores. This request runs
          synchronously and may take a while for a large number of stores -- keep this window open until it
          completes.
        </Typography>

        <Button variant="outlined" onClick={downloadTemplate} disabled={isSubmitting} sx={{ mb: 2 }}>
          Download Allocation Template
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          For every store and item, fill in the Billing Location Code, Company, Vendor Code, and Qty (the quantity
          that vendor is fulfilling -- one item can appear on multiple rows split across vendors, but each
          store/item's row quantities must add up to exactly what the PR asked for), then upload it below.
        </Typography>

        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => setFile(e.target.files[0] || null)}
          disabled={isSubmitting}
        />

        <FormControl sx={{ mt: 2 }} disabled={isSubmitting}>
          <FormLabel id="bulk-po-submit-mode-label">
            Applies to every PO this run creates (skipped/failed items are never submitted either way)
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="bulk-po-submit-mode-label"
            value={submitMode}
            onChange={(e) => setSubmitMode(e.target.value)}
          >
            <FormControlLabel value="draft" control={<Radio />} label="Save as Draft" />
            <FormControlLabel value="approval" control={<Radio />} label="Send for Approval" />
          </RadioGroup>
        </FormControl>

        <Button
          variant="outlined"
          color="success"
          fullWidth
          sx={{ mt: 2 }}
          disabled={isSubmitting || !file}
          onClick={generateBulkPOs}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              Generating Purchase Orders...
            </>
          ) : (
            "Generate Bulk POs"
          )}
        </Button>
      </div>
    </CustomModal>
  );
};

export default BulkPOModal;
